import type { Except, ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'
import type { ComponentProps } from 'react'

import { DateRangePicker } from '../../forms/datePicker/DateRangePicker'
import { addDays } from '../../forms/datePicker/internal/utils'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import type { FormikInputPropsForWrapper } from '../types'

export type FormikDateRangePickerProps = Except<
  ComponentProps<typeof DateRangePicker>,
  'id' | 'isInError' | 'onChange' | 'value' | 'wrapperProps'
> & {
  fromDateName: ValidatedKey
  toDateName: ValidatedKey
  /** Stores the to date as the day after the selected date, for backends with an exclusive upper bound. */
  isToDateInclusive?: boolean
} & FormikInputPropsForWrapper

export const FormikDateRangePicker = ({
  fromDateName,
  toDateName,
  isToDateInclusive = true,
  isRequired,
  label,
  description,
  validationText,
  onDefaultValueClick,
  hasRightColumnAlways,
  existingValueConfig,
  disabled,
  onStartBlur,
  onEndBlur,
  ...restProps
}: FormikDateRangePickerProps) => {
  const [fromField, fromMeta, fromHelpers] = useField<string | undefined>(
    fromDateName,
  )
  const [toField, toMeta, toHelpers] = useField<string | undefined>(toDateName)
  const fromError = useFormikErrorWithState(fromMeta)
  const toError = useFormikErrorWithState(toMeta)
  const toDateOffset = isToDateInclusive ? 1 : 0
  const displayedEnd = addDays(toField.value || undefined, -toDateOffset)
  const isFromRequired = useFormikIsRequired(fromDateName, isRequired)
  const isToRequired = useFormikIsRequired(toDateName, isRequired)

  const wrapperProps = getWrapperProps({
    label,
    existingValue: undefined,
    existingValueConfig,
    inputValueDifferenceFromExistingValue: undefined,
    commonWrapperProps: {
      isRequired: isFromRequired || isToRequired,
      description,
      validationText,
      onDefaultValueClick,
      hasRightColumnAlways,
      htmlFor: fromDateName,
      error: fromError.error ?? toError.error,
    },
  })

  return (
    <DateRangePicker
      {...restProps}
      id={fromDateName}
      wrapperProps={wrapperProps}
      value={{
        start: fromField.value || undefined,
        end: displayedEnd,
      }}
      isInError={fromError.isInError || toError.isInError}
      disabled={useFormikIsDisabled(disabled)}
      onStartBlur={() => {
        void fromHelpers.setTouched(true)
        onStartBlur?.()
      }}
      onEndBlur={() => {
        void toHelpers.setTouched(true)
        onEndBlur?.()
      }}
      onChange={({ start, end }) => {
        if (start !== (fromField.value || undefined)) {
          void fromHelpers.setValue(start)
        }
        if (end !== displayedEnd) {
          void toHelpers.setValue(addDays(end, toDateOffset))
        }
      }}
    />
  )
}
